// Client-side real-time recommendation and prioritization engine for Kayoki products
// Designed to be extremely lightweight, lightning-fast, and run entirely in-browser.

interface UserInteractionHistory {
  productViews: Record<string, { count: number; lastViewedAt: number }>;
  categoryViews: Record<string, number>;
  genderViews: Record<string, number>;
  actions: Record<string, { type: 'waitlist' | 'checkout' | 'buy'; count: number; timestamp: number }>;
  scentNotesAffinity: Record<string, number>;
}

const STORAGE_KEY = "kayoki_recommendation_profile_v1";

// List of standard olfactory notes to extract for scent profile matching
const OLFACTORY_KEYWORDS = [
  "musk", "bergamot", "citrus", "vanilla", "wood", "woods", "amber", "oud", "rose", "jasmine",
  "patchouli", "sandalwood", "cedar", "vetiver", "coconut", "fruit", "fruits", "floral", "iris",
  "neroli", "tonka", "spicy", "cardamom", "pepper", "saffron", "leather", "tobacco", "mint", "sea"
];

// Load history from localStorage safely
const loadHistory = (): UserInteractionHistory => {
  if (typeof window === "undefined") {
    return { productViews: {}, categoryViews: {}, genderViews: {}, actions: {}, scentNotesAffinity: {} };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    console.error("Failed to load recommendation profile:", e);
  }
  return { productViews: {}, categoryViews: {}, genderViews: {}, actions: {}, scentNotesAffinity: {} };
};

// Save history to localStorage safely
const saveHistory = (history: UserInteractionHistory) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch (e) {
    console.error("Failed to save recommendation profile:", e);
  }
};

// Parse notes to extract matching olfactory keyword tags
const extractOlfactoryKeywords = (p: any): string[] => {
  const text = `${p.name || ""} ${p.topNotes || ""} ${p.heartNotes || ""} ${p.baseNotes || ""} ${p.description || ""}`.toLowerCase();
  return OLFACTORY_KEYWORDS.filter(keyword => text.includes(keyword));
};

/**
 * Record a PDP view in real-time
 */
export const recordProductView = (product: any) => {
  if (!product || !product.id) return;
  
  // Use a setTimeout / requestIdleCallback to keep it non-blocking and out of the critical rendering path
  const recordJob = () => {
    const history = loadHistory();
    const now = Date.now();

    // 1. Update product views
    if (!history.productViews[product.id]) {
      history.productViews[product.id] = { count: 0, lastViewedAt: 0 };
    }
    history.productViews[product.id].count += 1;
    history.productViews[product.id].lastViewedAt = now;

    // 2. Update category views
    if (product.categoryId) {
      const catId = String(product.categoryId);
      history.categoryViews[catId] = (history.categoryViews[catId] || 0) + 1;
    }

    // 3. Update gender views
    if (product.gender) {
      const gender = String(product.gender);
      history.genderViews[gender] = (history.genderViews[gender] || 0) + 1;
    }

    // 4. Update scent note affinity
    const keywords = extractOlfactoryKeywords(product);
    keywords.forEach(keyword => {
      history.scentNotesAffinity[keyword] = (history.scentNotesAffinity[keyword] || 0) + 1;
    });

    saveHistory(history);
  };

  if (typeof window !== "undefined") {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => recordJob());
    } else {
      setTimeout(recordJob, 100);
    }
  }
};

/**
 * Record an intentional interaction (e.g. waitlist join, buy now)
 */
export const recordProductAction = (product: any, actionType: 'waitlist' | 'checkout' | 'buy') => {
  if (!product || !product.id) return;

  const recordJob = () => {
    const history = loadHistory();
    const now = Date.now();
    const actionKey = `${product.id}_${actionType}`;

    // 1. Update actions list
    if (!history.actions[actionKey]) {
      history.actions[actionKey] = { type: actionType, count: 0, timestamp: 0 };
    }
    history.actions[actionKey].count += 1;
    history.actions[actionKey].timestamp = now;

    // 2. Give extra boost to category views
    if (product.categoryId) {
      const catId = String(product.categoryId);
      history.categoryViews[catId] = (history.categoryViews[catId] || 0) + 5; // Extra weight for intent
    }

    // 3. Give extra boost to scent notes
    const keywords = extractOlfactoryKeywords(product);
    keywords.forEach(keyword => {
      history.scentNotesAffinity[keyword] = (history.scentNotesAffinity[keyword] || 0) + 3;
    });

    saveHistory(history);
  };

  if (typeof window !== "undefined") {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => recordJob());
    } else {
      setTimeout(recordJob, 50);
    }
  }
};

/**
 * Real-time dynamic ranking and prioritisation of candidate products.
 * Leverages content-based metrics paired with the user's real-time behavioral profile.
 */
export const rankAndPrioritizeProducts = (
  currentProduct: any,
  allProducts: any[],
  limit: number = 24
): any[] => {
  if (!allProducts || allProducts.length === 0) return [];

  const history = loadHistory();
  const currentId = currentProduct?.id;
  const currentCatId = currentProduct?.categoryId ? String(currentProduct.categoryId) : null;
  const currentGender = currentProduct?.gender;
  const currentKeywords = currentProduct ? extractOlfactoryKeywords(currentProduct) : [];
  
  // Parse current product price safely
  const parsePrice = (p: any): number => {
    if (!p || p.price === undefined) return 0;
    if (typeof p.price === "number") return p.price;
    const cleanStr = String(p.price).replace(/[^0-9]/g, "");
    const parsed = parseInt(cleanStr, 10);
    return isNaN(parsed) ? 0 : parsed;
  };

  const currentPrice = parsePrice(currentProduct);

  // Map and score all active candidates
  const scoredCandidates = allProducts
    .filter((p: any) => {
      // Must be tagged to a category, and not the current product itself
      const isNotCurrent = p.id !== currentId;
      const hasCategory = !!p.categoryId;
      return isNotCurrent && hasCategory;
    })
    .map((candidate: any) => {
      let score = 0;
      const candidateId = String(candidate.id);
      const candidateCatId = candidate.categoryId ? String(candidate.categoryId) : null;
      const candidateGender = candidate.gender;
      const candidatePrice = parsePrice(candidate);
      const candidateKeywords = extractOlfactoryKeywords(candidate);

      // --- SECTION 1: CONTENT SIMILARITY WITH CURRENT VIEWED ITEM ---

      // 1.1 Category match (Primary content signal)
      if (currentCatId && candidateCatId === currentCatId) {
        score += 20;
      }

      // 1.2 Gender compatibility
      if (currentGender && candidateGender) {
        if (candidateGender === currentGender) {
          score += 15;
        } else if (candidateGender === "Unisex" || currentGender === "Unisex") {
          score += 8;
        }
      }

      // 1.3 Olfactory (scent notes) similarity
      if (currentKeywords.length > 0 && candidateKeywords.length > 0) {
        const intersection = candidateKeywords.filter(k => currentKeywords.includes(k));
        score += intersection.length * 5; // +5 points for every matching fragrance note keyword
      }

      // 1.4 Price bracket similarity
      if (currentPrice > 0 && candidatePrice > 0) {
        const pctDiff = Math.abs(candidatePrice - currentPrice) / currentPrice;
        if (pctDiff <= 0.15) {
          score += 8; // Very close pricing bracket
        } else if (pctDiff <= 0.3) {
          score += 4; // Moderate price range match
        }
      }

      // --- SECTION 2: USER PROFILE AFFINITY (LOCAL BEHAVIORAL SIGNALS) ---

      // 2.1 Historical Category Affinity
      if (candidateCatId && history.categoryViews[candidateCatId]) {
        const viewsCount = history.categoryViews[candidateCatId];
        score += Math.min(viewsCount * 3, 24); // Cap max category view boost to prevent feedback loops
      }

      // 2.2 Historical Gender Affinity
      if (candidateGender && history.genderViews[candidateGender]) {
        const genderViewsCount = history.genderViews[candidateGender];
        score += Math.min(genderViewsCount * 2, 16);
      }

      // 2.3 Scent Notes Affinity
      if (candidateKeywords.length > 0) {
        let scentScore = 0;
        candidateKeywords.forEach(keyword => {
          if (history.scentNotesAffinity[keyword]) {
            scentScore += history.scentNotesAffinity[keyword] * 1.5;
          }
        });
        score += Math.min(scentScore, 20); // Cap scent affinity boost
      }

      // 2.4 Product Recency and Conversion Retargeting
      const viewStat = history.productViews[candidateId];
      if (viewStat) {
        // User viewed this candidate before but hasn't bought it yet
        score += 6; // Retarget interest
        
        // However, if they've viewed it excessively (e.g. > 6 times) but never clicked buy,
        // decay the score slightly to introduce novelty
        if (viewStat.count > 6) {
          score -= 10;
        }
      } else {
        // Discovery / novelty bonus (slight preference for fresh, unviewed products to optimize click-throughs)
        score += 4;
      }

      // 2.5 Checkout / Conversion Boost
      // If user added to waitlist or checked out any product in this candidate's category, boost candidate.
      if (candidateCatId) {
        const hasConversionInCategory = Object.keys(history.actions).some(actionKey => {
          const prodId = actionKey.split('_')[0];
          const matchedProd = allProducts.find(item => String(item.id) === prodId);
          return matchedProd && String(matchedProd.categoryId) === candidateCatId;
        });
        if (hasConversionInCategory) {
          score += 12;
        }
      }

      // 2.6 Deterministic hash-based tie breaker to avoid unstable list layout shifts
      const strId = String(candidate.id);
      let hash = 0;
      for (let i = 0; i < strId.length; i++) {
        hash = (hash << 5) - hash + strId.charCodeAt(i);
        hash |= 0;
      }
      const tieBreaker = Math.abs(hash % 100) / 100;
      
      return {
        product: candidate,
        score: score + tieBreaker
      };
    });

  // Sort candidates by score in descending order and select the top requested count
  const sorted = scoredCandidates.sort((a, b) => b.score - a.score);

  return sorted.slice(0, limit).map(item => item.product);
};
